/**
 * glossary.ts — Phase 290 (Glossary & Definition Popovers), D-01 — the client half of the
 * glossary. Binds a Bootstrap Popover to every server-rendered glossary trigger.
 *
 * Markup contract (produced server-side by plan 290-03, fixed here so the two plans run in
 * parallel): a trigger is `<button type="button" class="glossary-trigger[ extra classes]"
 * data-glossary-key="…" data-glossary-title="…" data-glossary-content="…">visible term</button>`.
 * `data-glossary-content` is App\Utils\Glossary's plain-text output: a plain line, "\n\n", a
 * "Precisely: …" line, optionally "\n\n" + a "Here: …" line. A bare term with no definition
 * (D-03) is plain text with no `<button>` at all — this module never sees it.
 *
 * Two deliberate departures from 290-UI-SPEC.md's "locked" popover snippet (both inside
 * CONTEXT.md's Claude's-Discretion scope, both evidenced from this repo):
 *
 * 1. The trigger option below is set to 'click focus', not the UI-SPEC's 'focus' alone. Safari
 *    (macOS and iOS) does not focus a <button>
 *    when it is clicked or tapped, and Bootstrap's own docs say a focus-dismiss popover needs an
 *    `<a tabindex="0">` for that reason. D-02 locks a <button>, and SC-4's whole point is the
 *    phone. With 'click focus', a tap opens it through the click path
 *    (node_modules/bootstrap/js/src/tooltip.js lines 449-453), Tab still opens it through focus
 *    (lines 462-466), and hide() resets both active triggers (lines 263-265) so Escape and
 *    outside-tap close it cleanly.
 * 2. `data-glossary-title` / `data-glossary-content` are used instead of Bootstrap's own
 *    normalized attribute-prefix pair for the same options.
 *    Bootstrap feeds every one of those prefixed attributes through normalizeData()
 *    (node_modules/bootstrap/js/src/dom/manipulator.js lines 8-34), which turns "12" into a
 *    Number and runs JSON.parse(decodeURIComponent(…)) on everything else. A title like "n" is
 *    safe, but a definition containing a %xx sequence would be silently decoded. Private
 *    attributes passed as explicit string options bypass that entirely.
 *
 * Idempotent: `initGlossary()` is safe to call more than once (Popover.getInstance() guard per
 * element, module-level flag for the document listeners), matching questDetail.ts's own
 * idempotency idiom (src/js/mc/questDetail.ts lines 483-510). Content is always a plain string
 * and the popover's `html` option is never turned on (T-290-05) — Bootstrap writes it via
 * textContent, so markup in an attribute can never execute.
 *
 * 290-13 gap-closure fix (SC-4/T-290-50, the rolled-back 290-10 deploy's Escape regression): the
 * currently-open trigger used to be recorded only once its popover had FINISHED fading in, which
 * left a real window — the length of the fade transition — where a keyboard user could already
 * see the popover on screen but this module did not yet consider anything "open". Escape pressed
 * in that window found nothing to close and did nothing, so the popover finished appearing and
 * then sat there, unreachable by keyboard. The tracking is now updated the moment a popover
 * starts opening rather than once it has fully appeared, so Escape can always find and dismiss it
 * regardless of how far along the fade is. Confirmed against a local event timeline and fixed
 * without touching the locked popover configuration below.
 */

import { Popover } from 'bootstrap';

const TRIGGER_SELECTOR = 'button.glossary-trigger[data-glossary-key]';
const POPOVER_SELECTOR = '.glossary-popover';

let documentListenersBound = false;
let openTrigger: HTMLElement | null = null;

function isGlossaryTrigger(el: Element | null): el is HTMLElement {
    return el !== null && el instanceof HTMLElement && el.matches(TRIGGER_SELECTOR);
}

function hideOpenTrigger(): void {
    if (openTrigger === null) {
        return;
    }
    Popover.getInstance(openTrigger)?.hide();
}

function bindDocumentListeners(): void {
    if (documentListenersBound) {
        return;
    }
    documentListenersBound = true;

    // show.bs.popover bubbles from the trigger (Bootstrap events bubble —
    // node_modules/bootstrap/js/src/dom/event-handler.js). One glossary popover open at a time:
    // hide whichever one is currently open before letting a different one show.
    //
    // openTrigger is set HERE, on `show`, not on `shown` (290-13 root-cause fix). Bootstrap's
    // own tooltip.js adds the tip's `show` CSS class synchronously inside show(), before the
    // fade-in transition completes and before `shown.bs.popover` fires (tooltip.js `show()`/
    // `_isShown()`: `_isShown()` is `tip.classList.contains('show')`, true the instant `show()`
    // adds that class). `hide()` only bails out via its own `if (!this._isShown()) return`
    // guard — it does NOT require the transition to have finished. Tracking `openTrigger` from
    // `shown` instead (the pre-290-13 code) left a real gap: a keyboard user's Tab lands on a
    // trigger and opens it well inside the ~150ms fade window (confirmed by a local event
    // timeline, 290-13-SUMMARY.md), so an Escape pressed in that window found `openTrigger`
    // still `null` and `hideOpenTrigger()` was a no-op — the popover then finished fading in and
    // stayed open, undismissable by Escape, exactly the prod SC-4 regression. Setting it on
    // `show` closes that window: `hideOpenTrigger()` can call `.hide()` mid-fade and Bootstrap
    // reverses the transition correctly (proven locally, GREEN).
    document.addEventListener('show.bs.popover', (event: Event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement) || !isGlossaryTrigger(target)) {
            return;
        }
        if (openTrigger !== null && openTrigger !== target) {
            Popover.getInstance(openTrigger)?.hide();
        }
        openTrigger = target;
    });

    document.addEventListener('hidden.bs.popover', (event: Event) => {
        const target = event.target;
        if (target instanceof HTMLElement && isGlossaryTrigger(target) && openTrigger === target) {
            openTrigger = null;
        }
    });

    document.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            hideOpenTrigger();
        }
    });

    // pointerdown, not click — iOS Safari does not deliver `click` to `document` for taps on
    // non-interactive elements. Closes the open popover on a tap/click outside both the trigger
    // and the popover itself.
    document.addEventListener('pointerdown', (event: PointerEvent) => {
        if (openTrigger === null) {
            return;
        }
        const target = event.target;
        if (!(target instanceof Node)) {
            return;
        }
        const insideTrigger = openTrigger.contains(target);
        const insidePopover = target instanceof Element && target.closest(POPOVER_SELECTOR) !== null;
        if (!insideTrigger && !insidePopover) {
            hideOpenTrigger();
        }
    });
}

/**
 * Binds a Popover to every `button.glossary-trigger[data-glossary-key]` under `root` that does
 * not already have one, and binds the document-level one-open/Escape/outside-tap listeners once
 * per page. Never throws — every lookup that can miss is null-checked.
 */
export function initGlossary(root: ParentNode = document): void {
    bindDocumentListeners();

    const triggers = root.querySelectorAll<HTMLElement>(TRIGGER_SELECTOR);

    triggers.forEach((el) => {
        // Idempotency (D-01): skip an element that already has a Popover instance.
        if (Popover.getInstance(el)) {
            return;
        }

        // Belt-and-braces for SC-5 — the server never emits a trigger with no content, but an
        // empty popover must be impossible even if it did (T-290-07).
        const content = el.dataset.glossaryContent;
        if (content === undefined || content === '') {
            return;
        }

        new Popover(el, {
            trigger: 'click focus',
            html: false,
            container: 'body',
            customClass: 'glossary-popover',
            placement: 'top',
            fallbackPlacements: ['bottom', 'top'],
            title: el.dataset.glossaryTitle ?? '',
            content,
        });
    });
}
